import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const DepositModal = ({ currency, onClose }) => {
  const [selectedNetwork, setSelectedNetwork] = useState(currency?.network || 'Bitcoin');
  const [qrCodeVisible, setQrCodeVisible] = useState(true);

  if (!currency) return null;

  const networks = currency.isFiat 
    ? [
        { id: 'ach', name: 'ACH Bank Transfer', fee: '$0', time: '1-3 business days' },
        { id: 'wire', name: 'Wire Transfer', fee: '$25', time: '1 business day' }
      ]
    : [
        { id: 'mainnet', name: currency.network, fee: 'Network fee applies', time: '6-12 confirmations' }
      ];

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(currency.depositAddress);
    // You could add a toast notification here
  };

  const generateQRCode = (address) => {
    // In a real app, you'd use a QR code library
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(address)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-trading border border-border w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Image
              src={currency.icon}
              alt={currency.name}
              className="w-8 h-8 rounded-full"
            />
            <div>
              <h2 className="text-lg font-heading-semibold text-text-primary">
                Deposit {currency.symbol}
              </h2>
              <p className="text-sm text-text-secondary">{currency.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
          >
            <Icon name="X" size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Network Selection */}
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-3">
              Select Network
            </label>
            <div className="space-y-2">
              {networks.map((network) => (
                <button
                  key={network.id}
                  onClick={() => setSelectedNetwork(network.name)}
                  className={`w-full flex items-center justify-between p-3 border rounded-trading transition-all duration-trading-fast ${
                    selectedNetwork === network.name
                      ? 'border-primary bg-primary-50' :'border-border hover:border-secondary-300'
                  }`}
                >
                  <div className="text-left">
                    <p className="font-body-medium text-text-primary">{network.name}</p>
                    <p className="text-sm text-text-secondary">
                      Fee: {network.fee} • Time: {network.time}
                    </p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedNetwork === network.name
                      ? 'border-primary bg-primary' :'border-secondary-300'
                  }`}>
                    {selectedNetwork === network.name && (
                      <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {currency.isFiat ? (
            /* Fiat Deposit Instructions */
            <div className="space-y-4">
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-trading">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-primary mt-0.5" />
                  <div>
                    <p className="font-body-medium text-primary-700 mb-1">
                      Bank Transfer Instructions
                    </p>
                    <p className="text-sm text-primary-600">
                      Use the following details to transfer funds from your bank account.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-trading">
                  <span className="text-sm text-text-secondary">Account Name</span>
                  <span className="font-data text-sm text-text-primary">CryptoTrade Pro</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-trading">
                  <span className="text-sm text-text-secondary">Account Number</span>
                  <span className="font-data text-sm text-text-primary">1234567890</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-trading">
                  <span className="text-sm text-text-secondary">Routing Number</span>
                  <span className="font-data text-sm text-text-primary">021000021</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-trading">
                  <span className="text-sm text-text-secondary">Reference</span>
                  <span className="font-data text-sm text-text-primary">USER123456</span>
                </div>
              </div>
            </div>
          ) : (
            /* Crypto Deposit Instructions */
            <div className="space-y-4">
              <div className="p-4 bg-warning-50 border border-warning-200 rounded-trading">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                  <div>
                    <p className="font-body-medium text-warning-700 mb-1">Important Notice</p>
                    <p className="text-sm text-warning-600">
                      Only send {currency.symbol} to this address. Sending other cryptocurrencies may result in permanent loss.
                    </p>
                  </div>
                </div>
              </div>

              {/* QR Code */}
              {qrCodeVisible && (
                <div className="text-center">
                  <div className="inline-block p-4 bg-white border border-border rounded-trading">
                    <Image
                      src={generateQRCode(currency.depositAddress)}
                      alt="Deposit QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                  <button
                    onClick={() => setQrCodeVisible(false)}
                    className="mt-2 text-sm text-text-secondary hover:text-text-primary"
                  >
                    Hide QR Code
                  </button>
                </div>
              )}

              {!qrCodeVisible && (
                <button
                  onClick={() => setQrCodeVisible(true)}
                  className="w-full flex items-center justify-center space-x-2 p-3 border border-border rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
                >
                  <Icon name="QrCode" size={16} className="text-text-secondary" />
                  <span className="text-sm text-text-secondary">Show QR Code</span>
                </button>
              )}

              {/* Deposit Address */}
              <div>
                <label className="block text-sm font-body-medium text-text-secondary mb-2">
                  Deposit Address
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={currency.depositAddress}
                    readOnly
                    className="flex-1 px-3 py-2 border border-border rounded-trading bg-secondary-50 text-text-primary font-data text-sm"
                  />
                  <button
                    onClick={handleCopyAddress}
                    className="px-3 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
                  >
                    <Icon name="Copy" size={16} />
                  </button>
                </div>
              </div>

              {/* Minimum Deposit */}
              <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-trading">
                <span className="text-sm text-text-secondary">Minimum Deposit</span>
                <span className="font-data text-sm text-text-primary">
                  {currency.minWithdrawal} {currency.symbol}
                </span>
              </div>

              <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-trading">
                <span className="text-sm text-text-secondary">Required Confirmations</span>
                <span className="font-data text-sm text-text-primary">
                  {currency.confirmations}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-trading font-body-medium text-text-primary hover:bg-secondary-50 transition-colors duration-trading-fast"
            >
              Close
            </button>
            {!currency.isFiat && (
              <button
                onClick={handleCopyAddress}
                className="flex-1 bg-primary text-white px-4 py-2 rounded-trading font-body-medium hover:bg-primary-600 transition-colors duration-trading-fast"
              >
                Copy Address
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositModal;